/**
 *
 * PackageContactForm
 *
 */

import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { submitForm } from '../../actions';
import PackageContactForm from './PackageContactForm';
import PackageContactFormTwoColumns from './PackageContactFormTwoColumns';
import { isValidIsoDate, parseIso } from 'Utils/dates';
import { isWithinInterval } from 'date-fns';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { debounce } from 'lodash';
import useQueryString from '../../../../Utils/hooks/useQueryString';

export const formKeys = Object.freeze({
  date: 'date',
  prefers: 'prefers',
  name: 'name',
  email: 'email',
  phoneCode: 'phoneCode',
  phone: 'phone',
  query: 'query',
  people: 'people'
});

const initialPeople = {
  adultsCount: 2,
  childrenQty: 0,
  kidsAges: []
};

const selector = ({ appConfiguration, places, packages }) => ({
  phoneTypes: appConfiguration.sharedConfig.phoneTypes,
  assistancePrefers: appConfiguration.sharedConfig.assistancePrefers,
  countries: places.countries,
  localize: appConfiguration.localize,
  filteredDate: packages.filteredDate,
  pricesByMonth: packages.packageItemData?.pricesByMonth
});

const ConnectedPackageContactForm = ({
  twoColumns = false,
  title,
  ...props
}) => {
  const dispatch = useDispatch();
  const actions = {
    submitForm: formState => dispatch(submitForm(formState))
  };
  const state = useSelector(selector);

  const [queryDate, onSetQueryDate] = useQueryString('date');

  const { countries, localize, filteredDate, pricesByMonth } = state;
  const codeLocalize = localize && localize['phoneCode'];
  const listPhoneCodes =
    countries &&
    countries.map(country => ({
      id: `${country.phoneCode}`,
      name: `${country.name} ${country.phoneCode}`
    }));

  const defaultPhoneCode =
    countries &&
    listPhoneCodes.find(phoneCode => phoneCode.id === codeLocalize);

  const { prices } = props;
  let price = null;
  let currency = null;
  const firstPriceDate =
    prices &&
    prices.length > 0 &&
    prices.reduce((prev, curr) =>
      prev.price.amount < curr.price.amount ? prev : curr
    ).startDate;

  let filteredPriceDate = null;

  if (filteredDate) {
    const monthSplit = filteredDate.split('-')[1];
    filteredPriceDate =
      !isNaN(+monthSplit) && pricesByMonth && pricesByMonth.length > 0
        ? pricesByMonth.find(month => month.month === +monthSplit)
        : null;
  }

  const {
    values,
    errors,
    setFieldValue,
    handleSubmit,
    touched,
    setFieldTouched
  } = useFormik({
    initialValues: {
      email: '',
      date:
        (isValidIsoDate(queryDate) && queryDate) ||
        filteredPriceDate?.date ||
        filteredDate ||
        firstPriceDate,
      prefers: null,
      name: '',
      phoneCode: defaultPhoneCode,
      phone: '',
      query: '',
      people: initialPeople
    },
    validateOnBlur: false,
    validateOnChange: false,
    validationSchema: Yup.object({
      name: Yup.string().required('Nombre es requerido'),
      email: Yup.string()
        .trim()
        .email('La dirección de correo no es válida')
        .required('El email es requerido'),
      date: Yup.string().required('La fecha es requerida'),
      prefers: Yup.object().typeError('Por favor elija que prefiere'),
      phone: Yup.string().required('Teléfono es requerido'),
      phoneCode: Yup.object()
        .typeError('Prefijo es requerido')
        .required('Prefijo es requerido'),
      people: Yup.object().shape({
        adultsCount: Yup.number().required(),
        childrenQty: Yup.number(),
        kidsAges: Yup.array().of(Yup.object())
      })
    }),
    enableReinitialize: true,
    onSubmit: values => {
      actions.submitForm({ ...values, price, email: values.email.trim() });
    }
  });

  if (prices && values.date) {
    const priceFound = prices.find(price => {
      if (price.startDate && price.endDate) {
        const startDate = parseIso(price.startDate);
        const endDate = parseIso(price.endDate);
        return isWithinInterval(parseIso(values.date), {
          start: startDate,
          end: endDate
        });
      } else {
        return values.date === price.startDate;
      }
    });
    if (priceFound) {
      price = priceFound.price.amount;
      currency = priceFound.price.currency;
    }
  }

  const FormComponent = twoColumns
    ? PackageContactFormTwoColumns
    : PackageContactForm;

  const handleChange = key => value => {
    setFieldValue(key, value);
    key === 'date' && onSetQueryDate(value);
    setFieldTouched(key, true);
  };

  return (
    <FormComponent
      {...props}
      {...state}
      {...values}
      errors={errors}
      onChange={handleChange}
      onSubmit={debounce(handleSubmit, 200)}
      actions={actions}
      listPhoneCodes={listPhoneCodes}
      price={price}
      currency={currency}
      touched={touched}
      title={title}
    />
  );
};

ConnectedPackageContactForm.propTypes = {
  twoColumns: PropTypes.bool,
  prices: PropTypes.arrayOf(
    PropTypes.shape({
      startDate: PropTypes.string.isRequired,
      endDate: PropTypes.string.isRequired,
      price: PropTypes.shape({
        amount: PropTypes.number.isRequired,
        currency: PropTypes.string.isRequired
      })
    })
  ),
  title: PropTypes.string
};

export default ConnectedPackageContactForm;
export { PackageContactForm };
